/**
 * Générateur — Établir des égalités de fractions
 * Attendu ID : 627 | Niveau : 6ème | Domaine : NC (Nombres et calculs)
 *
 * Ce générateur contient 8 EXERCICES DIDACTIQUEMENT DISTINCTS (chacun avec
 * son propre generatorKey au niveau exercice), déclinés en plusieurs formats UI :
 *
 *   1. `egalite_relier_disques`       → Relier (4 disques gauche ↔ 4 disques droite)
 *      Reconnaître visuellement deux fractions égales via aires coloriées.
 *
 *   2. `egalite_axes_gradues`         → Relier (3 points sur axe en b-ièmes ↔ axe en (b×k)-ièmes)
 *      Reconnaître deux points à la même abscisse sur deux demi-droites graduées.
 *
 *   3. `egalite_vrai_faux`            → V/F (6 items mixant règle correcte et erreurs classiques)
 *      Juger l'égalité entre deux écritures fractionnaires.
 *
 *   4. `egalite_decomposition`        → texte_trou_saisie + texte + qcm_unique
 *      Compléter/retrouver le coefficient k dans a/b = (a×k)/(b×k).
 *
 *   5. `egalite_trou_direct`          → qcm_unique + texte
 *      Compléter a/b = ?/c ou a/b = c/? sans décomposition explicite.
 *
 *   6. `egalite_trou_entier`          → qcm_unique + texte
 *      Compléter n = ?/k ou n = n/1 = ?/k.
 *
 *   7. `egalite_classer_valeur`       → Classer (8 fractions dans 3 colonnes cibles)
 *      Ranger des fractions selon leur valeur commune.
 *
 *   8. `egalite_reconnaitre_liste`    → QCM multiple
 *      Cocher toutes les fractions égales à une cible donnée.
 *
 * Règles dures de la fiche cluster 15 (attendu 627) :
 *   - POOL_A : fractions irréductibles (pgcd(a,b)=1), max(a,b) ≤ 12, b ≥ 2
 *   - POOL k : k ∈ [2, 10], b×k ≤ 100, a×k ≤ 100
 *   - POOL_B : fractions étendues où le diviseur commun est visible, max ≤ 60
 *   - POOL_C : entiers n ∈ [2, 10], k ∈ [2, 10], n×k ≤ 60
 *   - POOL_D : triplets de cibles pour classement, 2-3 représentants par cible
 *   - POOL_E : items V/F (règle correcte, additif, inversion, décimal, coefficient croisé, égalité à 1)
 *
 * Distracteurs contextualisés (fiche §4) :
 *   1. Erreur additive : (a+k)/(b+k) ou a+k au lieu de a×k
 *   2. Asymétrie multiplicative : a×k mais b inchangé (ou l'inverse)
 *   3. Inversion num/dén : a/b → b/a
 *   4. Oubli du multiplicateur : retour à la valeur de départ
 *   5. Confusion coefficient/résultat : k au lieu de a×k
 *   6. Confusion fraction/décimal : 4/5 = 4,5
 *   7. Répétition du même chiffre : confusion égalité à 1 / symétrie
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou, makeRelier, makeClasser,
} from '../../../utils';
import { gcd, fmtFrac, fmtKatex, fmtItem, fmtItemBrut } from './fraction';

// ═══════════════════════════════════════════════════════════
// CONSTANTES & LABELS
// ═══════════════════════════════════════════════════════════

const ATTENDU_ID = 627;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_RELIER_DISQ  = 'Relier des disques fractionnés égaux';
const L_RELIER_AXES  = 'Points à la même abscisse sur deux axes gradués';
const L_VF           = 'Juger = ou ≠ entre deux écritures fractionnaires';
const L_DECOMP_TTS   = 'Compléter une égalité avec décomposition (texte à trous)';
const L_DECOMP_SAI   = 'Trouver le coefficient d\'une égalité étendue (Saisie)';
const L_DECOMP_QCM   = 'Trouver le coefficient d\'une égalité étendue (QCM)';
const L_TROU_QCM     = 'Compléter une égalité à trou (QCM)';
const L_TROU_SAI     = 'Compléter une égalité à trou (Saisie)';
const L_ENT_QCM      = 'Compléter une égalité avec un entier (QCM)';
const L_ENT_SAI      = 'Compléter une égalité avec un entier (Saisie)';
const L_CLASSER      = 'Classer des fractions selon leur valeur commune';
const L_RECO_LIST    = 'Reconnaître les fractions égales dans une liste';

// ═══════════════════════════════════════════════════════════
// HELPERS ARITHMÉTIQUES
// ═══════════════════════════════════════════════════════════

/** Test d'irréductibilité : pgcd(a, b) === 1. */
const isIrreducible = (a, b) => gcd(a, b) === 1;

/**
 * KaTeX BRUT sans simplification (revue 2026-08-02) : fmtKatex passe par
 * mkFrac et écrasait n/n en « 1 » et a·k/b·k en a/b — quatre familles
 * d'items V/F affichaient « 10/9 = 10/9 » ou « 1 = 1 ». Les items V/F
 * comparent des ÉCRITURES : elles doivent rester telles quelles.
 */
const fmtKatexBrut = (n, d) => `\\dfrac{${n}}{${d}}`;

/** Test d'égalité entre deux fractions a/b et c/d : a*d === b*c. */
const fracEqual = (a, b, c, d) => a * d === b * c;

/** Construit un elements_additionnels avec une expression KaTeX pour l'énoncé. */
const exprElement = (katexStr) => ({
    version: 1,
    elements: [{ type: 'expression', config: { contenu: katexStr } }],
});

// ═══════════════════════════════════════════════════════════
// POOL_A — Fractions irréductibles simples
// ═══════════════════════════════════════════════════════════

/**
 * POOL_A — Liste des fractions de départ (a/b) irréductibles à utiliser dans
 * les exercices de complétion / décomposition. Triées par famille de dénominateur.
 * max(a, b) ≤ 12, pgcd(a, b) = 1, b ≥ 2.
 */
const POOL_A = [
    [1, 2], [1, 3], [2, 3], [1, 4], [3, 4],
    [1, 5], [2, 5], [3, 5], [4, 5],
    [1, 6], [5, 6],
    [1, 7], [2, 7], [3, 7], [4, 7], [5, 7], [6, 7],
    [1, 8], [3, 8], [5, 8], [7, 8],
    [1, 9], [2, 9], [4, 9], [5, 9], [7, 9], [8, 9],
    [1, 10], [3, 10], [7, 10], [9, 10],
    [1, 11], [2, 11], [3, 11], [4, 11], [5, 11], [6, 11], [7, 11],
    [1, 12], [5, 12], [7, 12], [11, 12],
    // Fractions impropres (a > b) pour couvrir les cas mentionnés au cours (7/3, 7/4, 5/4, 5/3…)
    [3, 2], [5, 2], [7, 2], [9, 2], [11, 2],
    [4, 3], [5, 3], [7, 3], [8, 3], [10, 3], [11, 3],
    [5, 4], [7, 4], [9, 4], [11, 4],
    [6, 5], [7, 5], [8, 5], [9, 5], [11, 5], [12, 5],
    [7, 6], [11, 6],
    [8, 7], [9, 7], [10, 7], [11, 7], [12, 7],
    [9, 8], [11, 8],
    [10, 9], [11, 9],
    [11, 10],
];

/** Vérification runtime de POOL_A : toutes irréductibles, b ≥ 2, max ≤ 12. */
for (const [a, b] of POOL_A) {
    if (!isIrreducible(a, b)) throw new Error(`POOL_A: fraction non irréductible ${a}/${b}`);
    if (b < 2) throw new Error(`POOL_A: dénominateur trop petit ${a}/${b}`);
    if (Math.max(a, b) > 12) throw new Error(`POOL_A: valeur trop grande ${a}/${b}`);
}

/**
 * Tire un couple (a, b) depuis POOL_A, optionnellement restreint.
 * @param {(a:number,b:number)=>boolean} filterFn - filtre optionnel
 */
const pickPoolA = (filterFn = null) => {
    const pool = filterFn ? POOL_A.filter(([a, b]) => filterFn(a, b)) : POOL_A;
    if (!pool.length) throw new Error('pickPoolA: pool vide');
    return randomChoice(pool);
};

/**
 * Tire un coefficient k ∈ [2, 10] tel que les contraintes soient respectées :
 *   b × k ≤ bMax, a × k ≤ aMax.
 * Retourne null si aucun k valide.
 */
const pickK = (a, b, kMin = 2, kMax = 10, bMax = 100, aMax = 100) => {
    const valid = [];
    for (let k = kMin; k <= kMax; k++) {
        if (b * k <= bMax && a * k <= aMax) valid.push(k);
    }
    return valid.length ? randomChoice(valid) : null;
};

// ═══════════════════════════════════════════════════════════
// POOL_B — Fractions simplifiables visibles (étendues de POOL_A)
// ═══════════════════════════════════════════════════════════

/**
 * Triplets [a×k, b×k, k, a, b] prêts à servir pour des exercices de simplification
 * ou de reconnaissance de forme étendue. Limité à max ≤ 60 pour la lisibilité.
 */
const buildPoolB = () => {
    const out = [];
    for (const [a, b] of POOL_A) {
        for (let k = 2; k <= 7; k++) {
            const extNum = a * k;
            const extDen = b * k;
            if (extNum <= 60 && extDen <= 60) {
                out.push({ extNum, extDen, k, a, b });
            }
        }
    }
    return out;
};
const POOL_B = buildPoolB();

// ═══════════════════════════════════════════════════════════
// POOL_C — Entiers pour egalite_trou_entier
// ═══════════════════════════════════════════════════════════

/**
 * Triplets [n, k, n×k] pour les exercices « n = ?/k ».
 * n ∈ [2, 10], k ∈ [2, 10], n × k ≤ 60.
 */
const buildPoolC = () => {
    const out = [];
    for (let n = 2; n <= 10; n++) {
        for (let k = 2; k <= 10; k++) {
            if (n * k <= 60) {
                out.push({ n, k, nk: n * k });
            }
        }
    }
    return out;
};
const POOL_C = buildPoolC();

// ═══════════════════════════════════════════════════════════
// POOL_D — Triplets de cibles pour egalite_classer_valeur
// ═══════════════════════════════════════════════════════════

/**
 * Triplets de 3 fractions cibles mutuellement distinctes (pas égales entre elles)
 * utilisées pour les exercices de classement. Chaque entrée est une liste de 3
 * couples [a, b] irréductibles.
 */
const POOL_D = [
    [[2, 3], [3, 4], [5, 6]],
    [[1, 2], [1, 3], [1, 4]],
    [[2, 5], [3, 5], [4, 5]],
    [[1, 2], [2, 3], [3, 4]],
    [[5, 4], [3, 2], [7, 4]],
    [[1, 3], [1, 4], [2, 5]],
    [[3, 4], [4, 5], [5, 6]],
    [[2, 3], [3, 5], [4, 7]],
];

/** Vérification : chaque cible d'un triplet est irréductible et distincte des autres. */
for (const triplet of POOL_D) {
    if (triplet.length !== 3) throw new Error('POOL_D: triplet de taille incorrecte');
    for (const [a, b] of triplet) {
        if (!isIrreducible(a, b)) throw new Error(`POOL_D: cible non irréductible ${a}/${b}`);
    }
    for (let i = 0; i < triplet.length; i++) {
        for (let j = i + 1; j < triplet.length; j++) {
            const [a1, b1] = triplet[i];
            const [a2, b2] = triplet[j];
            if (fracEqual(a1, b1, a2, b2)) {
                throw new Error(`POOL_D: cibles égales ${a1}/${b1} = ${a2}/${b2}`);
            }
        }
    }
}

// ═══════════════════════════════════════════════════════════
// POOL_E — Items V/F
// ═══════════════════════════════════════════════════════════

/**
 * Chaque item V/F est une fonction qui produit { texte, verdict }.
 * verdict : 0 = Vrai, 1 = Faux.
 * Le texte est un HTML inline avec KaTeX.
 */
const VF_ITEMS_V = [
    // Règle multiplicative appliquée correctement
    () => {
        const [a, b] = randomChoice(POOL_A.filter(([, b]) => b >= 2));
        const k = randInt(2, 6);
        return {
            texte: `$\\dfrac{${a} \\times ${k}}{${b} \\times ${k}} = ${fmtKatex(a, b)}$`,
            verdict: 0,
        };
    },
    // Simplification visible correcte (×k sur chaque côté) — côté gauche BRUT
    () => {
        const entry = randomChoice(POOL_B.filter(e => e.extDen <= 50));
        return {
            texte: `$${fmtKatexBrut(entry.extNum, entry.extDen)} = ${fmtKatex(entry.a, entry.b)}$`,
            verdict: 0,
        };
    },
    // Égalité à 1 : n/n = m/m — les deux côtés BRUTS
    () => {
        const n = randInt(2, 9);
        let m = randInt(2, 12);
        while (m === n) m = randInt(2, 12);
        return {
            texte: `$${fmtKatexBrut(n, n)} = ${fmtKatexBrut(m, m)}$`,
            verdict: 0,
        };
    },
    // Deux formes étendues égales (a/b étendu en ×k1 et ×k2) — les deux BRUTS
    () => {
        const [a, b] = randomChoice(POOL_A.filter(([a, b]) => Math.max(a, b) <= 8));
        const ks = [2, 3, 4, 5];
        const k1 = randomChoice(ks);
        let k2 = randomChoice(ks);
        while (k2 === k1) k2 = randomChoice(ks);
        const num1 = a * k1, den1 = b * k1;
        const num2 = a * k2, den2 = b * k2;
        if (den1 > 60 || den2 > 60) return null;
        return {
            texte: `$${fmtKatexBrut(num1, den1)} = ${fmtKatexBrut(num2, den2)}$`,
            verdict: 0,
        };
    },
];

const VF_ITEMS_F = [
    // Erreur additive (+k au num et +k au dén)
    () => {
        const [a, b] = randomChoice(POOL_A.filter(([a, b]) => a !== b && Math.max(a, b) <= 8));
        const k = randInt(2, 5);
        return {
            texte: `$\\dfrac{${a} + ${k}}{${b} + ${k}} = ${fmtKatex(a, b)}$`,
            verdict: 1,
        };
    },
    // Inversion num/dén
    () => {
        const [a, b] = randomChoice(POOL_A.filter(([a, b]) => a !== b && Math.max(a, b) <= 10));
        return {
            texte: `$${fmtKatex(a, b)} = ${fmtKatex(b, a)}$`,
            verdict: 1,
        };
    },
    // Confusion fraction / décimal (a/b lu comme "a,b")
    () => {
        const [a, b] = randomChoice(POOL_A.filter(([a, b]) => a < 10 && b < 10));
        return {
            texte: `$${fmtKatex(a, b)} = ${a}{,}${b}$`,
            verdict: 1,
        };
    },
    // Coefficient croisé (a×k2)/(b×k1) ≠ a/b
    () => {
        const [a, b] = randomChoice(POOL_A.filter(([a, b]) => a !== b && Math.max(a, b) <= 8));
        const k1 = randInt(2, 5);
        let k2 = randInt(2, 5);
        while (k2 === k1) k2 = randInt(2, 5);
        return {
            texte: `$\\dfrac{${a} \\times ${k2}}{${b} \\times ${k1}} = ${fmtKatex(a, b)}$`,
            verdict: 1,
        };
    },
    // Asymétrie : num multiplié par k, dén inchangé
    () => {
        const [a, b] = randomChoice(POOL_A.filter(([a, b]) => Math.max(a, b) <= 8));
        const k = randInt(2, 5);
        return {
            texte: `$\\dfrac{${a} \\times ${k}}{${b}} = ${fmtKatex(a, b)}$`,
            verdict: 1,
        };
    },
    // "Même chiffre partout" trompeur — côté gauche BRUT (9/9, pas « 1 »)
    () => {
        const pairs = [[4, 5], [3, 4], [5, 6], [2, 7], [3, 8]];
        const [a, b] = randomChoice(pairs);
        return {
            texte: `$${fmtKatexBrut(a + b, a + b)} = ${fmtKatex(a, b)}$`,
            verdict: 1,
        };
    },
];

// ═══════════════════════════════════════════════════════════
// HELPERS SVG (data URL pour compatibilité DOMPurify)
// ═══════════════════════════════════════════════════════════

// Couleurs claires pour le dark-mode (bg-slate-800/50 dans ImageElementEleve)
const SVG_STROKE = 'currentColor';
const SVG_TEXT = 'currentColor';

/** Helper : construit les elements_additionnels avec une image SVG data URL. */
const svgImageElement = (dataUrl, maxWidth = 'compact') => ({
    version: 1,
    elements: [{ type: 'image', config: { imageUrl: dataUrl, maxWidth } }],
});

/**
 * Génère un disque SVG divisé en `b` parts, dont `a` sont coloriées.
 * @param {number} a - parts coloriées
 * @param {number} b - nombre total de parts
 * @param {string} label - libellé affiché sous le disque (ex : "A", "B", ...)
 * @param {number} cx - centre x
 * @param {number} cy - centre y
 * @param {number} r - rayon
 */
const buildDisqueSvg = (a, b, label, cx, cy, r) => {
    const fillColor = '#fb923c';
    const emptyColor = 'rgba(255,255,255,0.1)';
    const parts = [];
    for (let i = 0; i < b; i++) {
        const startAngle = (i / b) * 2 * Math.PI - Math.PI / 2;
        const endAngle = ((i + 1) / b) * 2 * Math.PI - Math.PI / 2;
        const x1 = cx + r * Math.cos(startAngle);
        const y1 = cy + r * Math.sin(startAngle);
        const x2 = cx + r * Math.cos(endAngle);
        const y2 = cy + r * Math.sin(endAngle);
        const largeArc = (endAngle - startAngle) > Math.PI ? 1 : 0;
        const fill = i < a ? fillColor : emptyColor;
        parts.push(
            `<path d="M ${cx} ${cy} L ${x1.toFixed(2)} ${y1.toFixed(2)} A ${r} ${r} 0 ${largeArc} 1 ${x2.toFixed(2)} ${y2.toFixed(2)} Z" fill="${fill}" stroke="${SVG_STROKE}" stroke-width="1.5"/>`
        );
    }
    // Cas particulier b=1 : cercle plein
    if (b === 1) {
        parts.length = 0;
        parts.push(`<circle cx="${cx}" cy="${cy}" r="${r}" fill="${a >= 1 ? fillColor : emptyColor}" stroke="${SVG_STROKE}" stroke-width="1.5"/>`);
    }
    const labelY = cy + r + 16;
    parts.push(`<text x="${cx}" y="${labelY}" text-anchor="middle" font-size="14" fill="${SVG_TEXT}" font-weight="bold">${label}</text>`);
    return parts.join('');
};

/** Construit un data URL SVG contenant les 8 disques (4 gauche + 4 droite). */
const buildDisquesRelierSvg = (leftEntries, rightEntries) => {
    const r = 35;
    const svgs = [];
    const rowHeight = 90;
    const svgHeight = 4 * rowHeight + 20;
    const leftCx = 80;
    const rightCx = 260;
    for (let i = 0; i < 4; i++) {
        const cy = 40 + i * rowHeight;
        const leftLabel = ['A', 'B', 'C', 'D'][i];
        const rightLabel = ['E', 'F', 'G', 'H'][i];
        const [la, lb] = leftEntries[i];
        const [ra, rb] = rightEntries[i];
        svgs.push(buildDisqueSvg(la, lb, leftLabel, leftCx, cy, r));
        svgs.push(buildDisqueSvg(ra, rb, rightLabel, rightCx, cy, r));
    }
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 340 ${svgHeight}" width="340" height="${svgHeight}">${svgs.join('')}</svg>`;
    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

/**
 * Construit deux axes gradués superposés.
 * @param {number} b - dénominateur de l'axe supérieur
 * @param {number} k - coefficient (l'axe inférieur est gradué en b×k)
 * @param {Array<number>} topNums - numérateurs des points sur l'axe supérieur (A, B, C)
 * @param {Object} [options]
 * @param {boolean} [options.showBottomPoints=true] - afficher les points sur l'axe inférieur
 * @param {Array<number>} [options.bottomOrder] - ordre d'affichage des points sur l'axe inférieur
 */
const buildAxesGraduesSvg = (b, k, topNums, options = {}) => {
    const { showBottomPoints = true, bottomOrder = [0, 1, 2] } = options;
    const width = 520;
    const height = 180;
    const x0 = 30;
    const x1 = 490;
    const topMaxNum = Math.max(...topNums);
    const maxUnit = Math.ceil(topMaxNum / b) + 1;
    const pixelsPerUnit = (x1 - x0) / maxUnit;
    const yTop = 50;
    const yBottom = 130;
    const pixelForAbscisse = (num, den) => x0 + (num / den) * pixelsPerUnit;
    // Axe supérieur
    const topLines = [];
    topLines.push(`<line x1="${x0}" y1="${yTop}" x2="${x1}" y2="${yTop}" stroke="${SVG_STROKE}" stroke-width="2"/>`);
    topLines.push(`<polygon points="${x1},${yTop} ${x1 - 8},${yTop - 4} ${x1 - 8},${yTop + 4}" fill="${SVG_STROKE}"/>`);
    for (let u = 0; u <= maxUnit; u++) {
        const px = x0 + u * pixelsPerUnit;
        topLines.push(`<line x1="${px}" y1="${yTop - 6}" x2="${px}" y2="${yTop + 6}" stroke="${SVG_STROKE}" stroke-width="2"/>`);
        topLines.push(`<text x="${px}" y="${yTop - 10}" text-anchor="middle" font-size="12" fill="${SVG_TEXT}">${u}</text>`);
    }
    // Petites graduations en b-ièmes — agrandies et épaissies (revue
    // 2026-08-02 : ±3 px était à peine lisible, demande utilisateur)
    for (let u = 0; u < maxUnit; u++) {
        for (let i = 1; i < b; i++) {
            const px = x0 + (u + i / b) * pixelsPerUnit;
            topLines.push(`<line x1="${px}" y1="${yTop - 5}" x2="${px}" y2="${yTop + 5}" stroke="${SVG_STROKE}" stroke-width="1.5" opacity="0.7"/>`);
        }
    }
    // Points sur l'axe supérieur
    const topLabels = ['A', 'B', 'C'];
    for (let i = 0; i < topNums.length; i++) {
        const px = pixelForAbscisse(topNums[i], b);
        topLines.push(`<circle cx="${px}" cy="${yTop}" r="5" fill="#fb923c" stroke="${SVG_STROKE}" stroke-width="1.5"/>`);
        topLines.push(`<text x="${px}" y="${yTop + 22}" text-anchor="middle" font-size="14" fill="${SVG_TEXT}" font-weight="bold">${topLabels[i]}</text>`);
    }
    // Axe inférieur
    const bk = b * k;
    const bottomLines = [];
    bottomLines.push(`<line x1="${x0}" y1="${yBottom}" x2="${x1}" y2="${yBottom}" stroke="${SVG_STROKE}" stroke-width="2"/>`);
    bottomLines.push(`<polygon points="${x1},${yBottom} ${x1 - 8},${yBottom - 4} ${x1 - 8},${yBottom + 4}" fill="${SVG_STROKE}"/>`);
    for (let u = 0; u <= maxUnit; u++) {
        const px = x0 + u * pixelsPerUnit;
        bottomLines.push(`<line x1="${px}" y1="${yBottom - 6}" x2="${px}" y2="${yBottom + 6}" stroke="${SVG_STROKE}" stroke-width="2"/>`);
        bottomLines.push(`<text x="${px}" y="${yBottom + 18}" text-anchor="middle" font-size="12" fill="${SVG_TEXT}">${u}</text>`);
    }
    for (let u = 0; u < maxUnit; u++) {
        for (let i = 1; i < bk; i++) {
            const px = x0 + (u + i / bk) * pixelsPerUnit;
            bottomLines.push(`<line x1="${px}" y1="${yBottom - 5}" x2="${px}" y2="${yBottom + 5}" stroke="${SVG_STROKE}" stroke-width="1.5" opacity="0.7"/>`);
        }
    }
    // Points sur l'axe inférieur (optionnel)
    if (showBottomPoints) {
        const bottomLabels = ['D', 'E', 'F'];
        for (let j = 0; j < bottomOrder.length; j++) {
            const srcIdx = bottomOrder[j];
            const px = pixelForAbscisse(topNums[srcIdx] * k, bk);
            bottomLines.push(`<circle cx="${px}" cy="${yBottom}" r="5" fill="#fb923c" stroke="${SVG_STROKE}" stroke-width="1.5"/>`);
            bottomLines.push(`<text x="${px}" y="${yBottom - 12}" text-anchor="middle" font-size="14" fill="${SVG_TEXT}" font-weight="bold">${bottomLabels[j]}</text>`);
        }
    }
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}">${topLines.join('')}${bottomLines.join('')}</svg>`;
    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

// ═══════════════════════════════════════════════════════════
// GÉNÉRATEURS
// ═══════════════════════════════════════════════════════════

// ---- 1. egalite_relier_disques ----

const genRelierDisques = () => {
    // Tirer 4 fractions cibles distinctes (irréductibles simples avec b ∈ [2, 6] pour lisibilité)
    const candidates = POOL_A.filter(([a, b]) => b >= 2 && b <= 6 && a <= b);
    const chosen = shuffle(candidates).slice(0, 4);
    const leftEntries = chosen; // [a, b]
    // Pour chaque cible, tirer un coefficient k ∈ [2, 4] tel que l'aire droite reste lisible
    const ks = chosen.map(([a, b]) => {
        const valid = [];
        for (let k = 2; k <= 4; k++) {
            if (b * k <= 20) valid.push(k);
        }
        return valid.length ? randomChoice(valid) : 2;
    });
    const rightEntries = chosen.map(([a, b], i) => [a * ks[i], b * ks[i]]);
    // Mélange l'ordre d'affichage des disques droits
    const rightOrder = shuffle([0, 1, 2, 3]);
    const rightDisplay = rightOrder.map(idx => rightEntries[idx]);
    // Construire les paires : labels gauche = A, B, C, D dans l'ordre leftEntries
    // labels droit = E, F, G, H dans l'ordre rightDisplay
    // Le disque gauche i (label A+i) correspond au disque droit en position rightOrder.indexOf(i)
    const leftLabels = ['A', 'B', 'C', 'D'];
    const rightLabels = ['E', 'F', 'G', 'H'];
    const pairs = [];
    for (let i = 0; i < 4; i++) {
        const rightPos = rightOrder.indexOf(i);
        pairs.push({
            source: `$${leftLabels[i]} = ${fmtKatex(leftEntries[i][0], leftEntries[i][1])}$`,
            // Cible en écriture BRUTE (revue 2026-08-02, trouvé par l'utilisateur) :
            // fmtKatex re-simplifiait 3/18 en 1/6 — l'étiquette contredisait le
            // disque dessiné (18 parts, 3 coloriées) et l'appariement devenait
            // un matching d'écritures identiques.
            target: `$${rightLabels[rightPos]} = ${fmtKatexBrut(rightDisplay[rightPos][0], rightDisplay[rightPos][1])}$`,
        });
    }
    const elAdd = svgImageElement(buildDisquesRelierSvg(leftEntries, rightDisplay));
    const texte = `<p>Relie chaque disque de gauche (A, B, C, D) à celui de droite (E, F, G, H) qui représente la même fraction.</p>{{element:0}}`;
    const payload = makeRelier(pairs, texte, { ...opts(L_RELIER_DISQ), elements_additionnels: elAdd });
    payload._meta.generatorKey = 'egalite_relier_disques';
    return payload;
};

// ---- 2. egalite_axes_gradues ----

const genRelierAxes = () => {
    const b = randomChoice([3, 4]);
    const k = randomChoice([2, 3]);
    const bk = b * k;
    // Tirer 3 numérateurs distincts dans [1, 2b] (pour couvrir plusieurs unités)
    const poolNum = [];
    for (let n = 1; n <= 2 * b; n++) poolNum.push(n);
    const topNums = shuffle(poolNum).slice(0, 3);
    // Axe du bas : graduations visibles mais SANS points — sert de béquille visuelle
    const svg = buildAxesGraduesSvg(b, k, topNums, { showBottomPoints: false });
    // Paires : source = lettre du point (A, B, C), target = fraction étendue en $KaTeX$
    // L'élève lit la position sur l'axe du haut (ex: A = 1/3),
    // puis doit trouver l'équivalent en b×k-ièmes (ex: 2/6) parmi les targets.
    const topLabels = ['A', 'B', 'C'];
    const pairs = topNums.map((num, i) => ({
        source: topLabels[i],
        // Cible en (b×k)-ièmes NON simplifiée : fmtItem passe par mkFrac et ferait
        // retomber num·k/bk sur num/b (valeur de l'axe du haut) → aucune égalité
        // entre deux écritures, la tâche dégénère en lecture d'abscisse.
        target: fmtItemBrut(num * k, bk),
    }));
    const elAdd = svgImageElement(svg, 'small');
    const texte = `<p>L'axe du haut est gradué en ${b}-ièmes, l'axe du bas en ${bk}-ièmes. Relie chaque point à la fraction équivalente lue sur l'axe du bas.</p>{{element:0}}`;
    const payload = makeRelier(pairs, texte, { ...opts(L_RELIER_AXES), elements_additionnels: elAdd });
    payload._meta.generatorKey = 'egalite_axes_gradues';
    return payload;
};

// ---- 3. egalite_vrai_faux ----

const genVraiFaux = () => {
    // 2 vraies + 2 fausses : 6 lignes c'était trop (arbitrage user 2026-08-02,
    // même règle que le V/F de 626).
    const vShuffled = shuffle(VF_ITEMS_V);
    const fShuffled = shuffle(VF_ITEMS_F);
    const collected = [];
    for (const gen of vShuffled) {
        const item = gen();
        if (item && collected.filter(it => it.verdict === 0).length < 2) {
            collected.push(item);
        }
        if (collected.length >= 2) break;
    }
    for (const gen of fShuffled) {
        const item = gen();
        if (item && collected.filter(it => it.verdict === 1).length < 2) {
            collected.push(item);
        }
        if (collected.filter(it => it.verdict === 1).length >= 2) break;
    }
    // Complétion si on n'a pas assez d'items vrais ou faux (sécurité)
    while (collected.filter(it => it.verdict === 0).length < 2) {
        for (const gen of VF_ITEMS_V) {
            const it = gen();
            if (it) { collected.push(it); break; }
        }
    }
    while (collected.filter(it => it.verdict === 1).length < 2) {
        for (const gen of VF_ITEMS_F) {
            const it = gen();
            if (it) { collected.push(it); break; }
        }
    }
    // Garder exactement 2V + 2F
    const vItems = collected.filter(it => it.verdict === 0).slice(0, 2);
    const fItems = collected.filter(it => it.verdict === 1).slice(0, 2);
    const allItems = shuffle([...vItems, ...fItems]);
    const lignes = allItems.map(it => it.texte);
    const bonnesReponses = allItems.map(it => it.verdict);
    const payload = makeVraiFaux(
        lignes,
        bonnesReponses,
        `<p>Pour chaque ligne, dis si l'égalité est vraie ou fausse.</p>{{element:0}}`,
        opts(L_VF),
    );
    payload._meta.generatorKey = 'egalite_vrai_faux';
    return payload;
};

// ---- 4. egalite_decomposition (3 formats) ----

const tirerDecomposition = () => {
    // Tire une fraction (a, b) irréductible et un coefficient k valide.
    let a, b, k;
    for (let attempt = 0; attempt < 30; attempt++) {
        [a, b] = pickPoolA();
        k = pickK(a, b, 2, 10, 100, 100);
        if (k !== null) break;
    }
    return { a, b, k };
};

const genDecompositionTts = () => {
    const { a, b, k } = tirerDecomposition();
    const bk = b * k;
    const ak = a * k;
    // Phrase texte-à-trous : 3 trous — deux fois k puis ak.
    // Étais \rule (revue 2026-08-02, même fix que 620-626) : sans eux les
    // barres de fraction disparaissent sous les champs de saisie.
    const phrase = `$${fmtKatex(a, b)} = \\dfrac{${a} \\times {{${k}}}\\rule[-0.55em]{0pt}{0.55em}}{\\rule{0pt}{1.3em}${b} \\times {{${k}}}} = \\dfrac{{{${ak}}}\\rule[-0.55em]{0pt}{0.55em}}{\\rule{0pt}{1.3em}${bk}}$`;
    const payload = makeTexteTrou(
        phrase,
        `<p>Complète l'égalité en tapant le coefficient dans les deux premières cases, puis le résultat étendu.</p>`,
        { ...opts(L_DECOMP_TTS), mode: 'saisie', strict: false },
    );
    payload._meta.generatorKey = 'egalite_decomposition';
    return payload;
};

const genDecompositionSaisie = () => {
    const { a, b, k } = tirerDecomposition();
    const bk = b * k;
    const ak = a * k;
    // Membre droit en forme BRUTE (non simplifiée) : fmtKatex passe par mkFrac et
    // ferait retomber ak/bk sur a/b (identité) → k=1 lisible au lieu de k.
    const expr = `${fmtKatex(a, b)} = \\dfrac{${ak}}{${bk}}`;
    const texte = `<p>On sait que {{element:0}}. Quel est le coefficient <em>k</em> par lequel on multiplie à la fois le numérateur et le dénominateur\u00a0?</p>`;
    const payload = makeSaisie(String(k), texte, { ...opts(L_DECOMP_SAI), elements_additionnels: exprElement(expr) });
    payload._meta.generatorKey = 'egalite_decomposition';
    return payload;
};

const genDecompositionQcm = () => {
    const { a, b, k } = tirerDecomposition();
    const bk = b * k;
    const ak = a * k;
    // Membre droit en forme BRUTE (non simplifiée) : sinon fmtKatex(ak,bk) simplifie
    // en a/b et l'énoncé devient l'identité a/b = a/b (k=1 lisible, banque attend k).
    const expr = `${fmtKatex(a, b)} = \\dfrac{${ak}}{${bk}}`;
    const texte = `<p>On sait que {{element:0}}. Quel est le coefficient <em>k</em> par lequel on multiplie à la fois le numérateur et le dénominateur\u00a0?</p>`;
    // Distracteurs : a (confusion num source), b (confusion dén source), ak (confusion num étendu), bk (confusion dén étendu)
    const candidats = [a, b, ak, bk, k - 1, k + 1].filter(x => x !== k && x >= 1);
    const distinct = [...new Set(candidats.map(String))].filter(s => s !== String(k));
    const dists = shuffle(distinct).slice(0, 3).map(Number);
    while (dists.length < 3) {
        const cand = randInt(1, 20);
        if (cand !== k && !dists.includes(cand)) dists.push(cand);
    }
    const choix = [String(k), ...dists.map(String)];
    const payload = makeQcm(choix, String(k), texte, { ...opts(L_DECOMP_QCM), elements_additionnels: exprElement(expr) });
    payload._meta.generatorKey = 'egalite_decomposition';
    return payload;
};

// ---- 5. egalite_trou_direct (2 formats) ----

const tirerTrouDirect = () => {
    // Tire (a, b, k) et une position de trou (num ou dén).
    let a, b, k;
    for (let attempt = 0; attempt < 30; attempt++) {
        [a, b] = pickPoolA();
        k = pickK(a, b, 2, 10, 100, 100);
        if (k !== null) break;
    }
    const position = randomChoice(['num', 'den']);
    return { a, b, k, position };
};

const genTrouDirectQcm = () => {
    const { a, b, k, position } = tirerTrouDirect();
    const ak = a * k;
    const bk = b * k;
    let expr, bonne, dists;
    if (position === 'num') {
        // a/b = ?/bk, bonne = ak
        expr = `${fmtKatex(a, b)} = \\dfrac{?}{${bk}}`;
        bonne = ak;
        // Distracteurs : a+k (additif), a (oubli), bk (confusion num↔dén), b+k (additif inversé)
        const candidats = [a + k, a, bk, b + k, a * (k - 1), a + (bk - b)];
        dists = [...new Set(candidats.map(String).filter(s => Number(s) !== bonne && Number(s) >= 0))];
    } else {
        // a/b = ak/?, bonne = bk
        expr = `${fmtKatex(a, b)} = \\dfrac{${ak}}{?}`;
        bonne = bk;
        const candidats = [b + k, b, ak, a + k, b * (k - 1), b + (ak - a)];
        dists = [...new Set(candidats.map(String).filter(s => Number(s) !== bonne && Number(s) >= 0))];
    }
    const distsNum = shuffle(dists.map(Number)).slice(0, 3);
    while (distsNum.length < 3) {
        const cand = randInt(1, 100);
        if (cand !== bonne && !distsNum.includes(cand)) distsNum.push(cand);
    }
    const choix = [String(bonne), ...distsNum.map(String)];
    const texte = `<p>Complète l'égalité\u00a0: {{element:0}}</p>`;
    const payload = makeQcm(choix, String(bonne), texte, { ...opts(L_TROU_QCM), elements_additionnels: exprElement(expr) });
    payload._meta.generatorKey = 'egalite_trou_direct';
    return payload;
};

const genTrouDirectSaisie = () => {
    const { a, b, k, position } = tirerTrouDirect();
    const ak = a * k;
    const bk = b * k;
    let expr, bonne;
    if (position === 'num') {
        expr = `${fmtKatex(a, b)} = \\dfrac{?}{${bk}}`;
        bonne = ak;
    } else {
        expr = `${fmtKatex(a, b)} = \\dfrac{${ak}}{?}`;
        bonne = bk;
    }
    const texte = `<p>Complète l'égalité en tapant le nombre manquant\u00a0: {{element:0}}</p>`;
    const payload = makeSaisie(String(bonne), texte, { ...opts(L_TROU_SAI), elements_additionnels: exprElement(expr) });
    payload._meta.generatorKey = 'egalite_trou_direct';
    return payload;
};

// ---- 6. egalite_trou_entier (2 formats) ----

const tirerTrouEntier = () => {
    const { n, k, nk } = randomChoice(POOL_C);
    const avecEtape = Math.random() < 0.5;
    return { n, k, nk, avecEtape };
};

const genTrouEntierQcm = () => {
    const { n, k, nk, avecEtape } = tirerTrouEntier();
    const expr = avecEtape
        ? `${n} = \\dfrac{${n}}{1} = \\dfrac{?}{${k}}`
        : `${n} = \\dfrac{?}{${k}}`;
    const bonne = nk;
    // Distracteurs : n+k (additif), k (confusion num↔dén), n (oubli coef), n*k+k (décalage)
    const candidats = [n + k, k, n, nk + k, n + 1];
    const distinct = [...new Set(candidats.map(String))].filter(s => Number(s) !== bonne);
    const dists = shuffle(distinct).slice(0, 3).map(Number);
    while (dists.length < 3) {
        const cand = randInt(1, 100);
        if (cand !== bonne && !dists.includes(cand)) dists.push(cand);
    }
    const choix = [String(bonne), ...dists.map(String)];
    const texte = `<p>Complète l'égalité\u00a0: {{element:0}}</p>`;
    const payload = makeQcm(choix, String(bonne), texte, { ...opts(L_ENT_QCM), elements_additionnels: exprElement(expr) });
    payload._meta.generatorKey = 'egalite_trou_entier';
    return payload;
};

const genTrouEntierSaisie = () => {
    const { n, k, nk, avecEtape } = tirerTrouEntier();
    const expr = avecEtape
        ? `${n} = \\dfrac{${n}}{1} = \\dfrac{?}{${k}}`
        : `${n} = \\dfrac{?}{${k}}`;
    const texte = `<p>Complète l'égalité en tapant le nombre manquant\u00a0: {{element:0}}</p>`;
    const payload = makeSaisie(String(nk), texte, { ...opts(L_ENT_SAI), elements_additionnels: exprElement(expr) });
    payload._meta.generatorKey = 'egalite_trou_entier';
    return payload;
};

// ---- 7. egalite_classer_valeur ----

const genClasserValeur = () => {
    const triplet = randomChoice(POOL_D);
    // Pour chaque cible, tirer 2 ou 3 représentants étendus (k ∈ [2, 5])
    const catData = []; // données brutes pour validation
    for (const [a, b] of triplet) {
        const nbRepr = randInt(2, 3);
        const ks = [];
        while (ks.length < nbRepr) {
            const k = randInt(2, 5);
            if (!ks.includes(k)) ks.push(k);
        }
        const tuples = ks.map(k => [a * k, b * k]);
        const valid = tuples.every(([n, d]) => n <= 60 && d <= 60);
        const finalTuples = valid ? tuples : [2, 3].slice(0, nbRepr).map(k => [a * k, b * k]);
        catData.push({ a, b, tuples: finalTuples });
    }
    // Vérification : aucune fraction d'une catégorie ne doit être égale à une autre catégorie
    for (let i = 0; i < catData.length; i++) {
        for (const [n, d] of catData[i].tuples) {
            for (let j = 0; j < catData.length; j++) {
                if (i === j) continue;
                if (fracEqual(n, d, catData[j].a, catData[j].b)) {
                    throw new Error(`genClasserValeur: ambiguïté ${n}/${d} classable en ${catData[i].a}/${catData[i].b} ET ${catData[j].a}/${catData[j].b}`);
                }
            }
        }
    }
    // Formater en $KaTeX$ pour l'affichage
    // Items formatés SANS simplification : fmtItem simplifierait 2/6 en 1/3
    // et afficherait la réponse (= le nom de la colonne) → doublons + exercice trivial
    const categories = catData.map(({ a, b, tuples }) => ({
        name: fmtItem(a, b),
        items: tuples.map(([n, d]) => fmtItemBrut(n, d)),
    }));
    const texte = `<p>Range chaque fraction dans la colonne correspondant à la fraction qui lui est égale.</p>`;
    const payload = makeClasser(categories, texte, opts(L_CLASSER));
    payload._meta.generatorKey = 'egalite_classer_valeur';
    return payload;
};

// ---- 8. egalite_reconnaitre_liste ----

const genReconnaitreListe = () => {
    // Tire une fraction cible (a, b) irréductible
    const [a, b] = pickPoolA((a, b) => Math.max(a, b) <= 10);
    // 2 bonnes réponses, 3 distracteurs → 5 choix
    const nbBonnes = 2;
    const ksPool = [2, 3, 4, 5, 6, 7, 10];
    const ksCandidats = shuffle(ksPool).filter(k => a * k <= 80 && b * k <= 80);
    const ks = ksCandidats.slice(0, nbBonnes);
    const validKs = ks.length >= 2 ? ks : [2, 3];

    // Travailler en tuples [num, den] pour la validation, formater à la fin
    const bonnesTuples = validKs.map(k => [a * k, b * k]);

    const nbDists = 5 - bonnesTuples.length;
    const distTuples = [];
    // D1 : inversion num/dén (si a ≠ b). On exclut a === 1 : l'inversion donnerait
    // alors un ENTIER (ex. cible 1/10 → 10/1 = « 10 »), leurre éliminable sans maths.
    if (a !== b && a !== 1) distTuples.push([b, a]);
    // D2 : erreur additive (+j) pour j ∈ [1, 3]
    for (let j = 1; j <= 3; j++) {
        if (!fracEqual(a + j, b + j, a, b)) distTuples.push([a + j, b + j]);
    }
    // D3 : asymétrie — multiplier seulement le num
    for (const k of [2, 3]) {
        if (!fracEqual(a * k, b, a, b)) distTuples.push([a * k, b]);
    }
    // D4 : fraction étendue d'une AUTRE cible (piège fin)
    const otherTargets = POOL_A.filter(([ta, tb]) => !fracEqual(ta, tb, a, b) && Math.max(ta, tb) <= 8);
    for (const [ta, tb] of shuffle(otherTargets).slice(0, 3)) {
        const k = randInt(2, 5);
        if (ta * k <= 60 && tb * k <= 60 && !fracEqual(ta * k, tb * k, a, b)) {
            distTuples.push([ta * k, tb * k]);
        }
    }
    // Déduplique via clé string, retire celles égales à la cible
    const bonneKeys = new Set(bonnesTuples.map(([n, d]) => `${n}/${d}`));
    const seen = new Set(bonneKeys);
    const cleanDists = [];
    for (const [dn, dd] of distTuples) {
        const key = `${dn}/${dd}`;
        if (seen.has(key)) continue;
        if (fracEqual(dn, dd, a, b)) continue;
        if (dn <= 0 || dd <= 0) continue;
        cleanDists.push([dn, dd]);
        seen.add(key);
        if (cleanDists.length >= nbDists) break;
    }
    // Fallback
    while (cleanDists.length < nbDists) {
        const rn = randInt(1, 20);
        const rd = randInt(2, 20);
        const key = `${rn}/${rd}`;
        if (!seen.has(key) && !fracEqual(rn, rd, a, b)) {
            cleanDists.push([rn, rd]);
            seen.add(key);
        }
    }
    // Formater SANS SIMPLIFIER (fmtItemBrut) pour l'affichage, puis dédup sur le
    // FORMATÉ : fmtItem passerait par mkFrac et ramènerait 2/20, 3/30… à la cible
    // 1/10 elle-même → « équivalents » triviaux + bonne_reponse dupliquée [B,B].
    const bonnes = bonnesTuples.map(([n, d]) => fmtItemBrut(n, d));
    const distsFormatted = cleanDists.slice(0, nbDists).map(([n, d]) => fmtItemBrut(n, d));
    const allFormatted = [...bonnes, ...distsFormatted];
    const seenFmt = new Set();
    const uniqueChoix = allFormatted.filter(c => !seenFmt.has(c) && seenFmt.add(c));
    // Compléter si dédup a réduit sous 5 choix
    let bump = 4;
    while (uniqueChoix.length < 5 && bump < 30) {
        const filler = fmtItemBrut(a + bump, b + bump);
        if (!seenFmt.has(filler) && !fracEqual(a + bump, b + bump, a, b)) {
            seenFmt.add(filler);
            uniqueChoix.push(filler);
        }
        bump++;
    }
    const choix = shuffle(uniqueChoix);
    const texte = `<p>Parmi les fractions suivantes, coche celles qui sont égales à {{element:0}}.</p>`;
    const payload = makeQcm(choix, bonnes, texte, { ...opts(L_RECO_LIST), elements_additionnels: exprElement(fmtKatex(a, b)) }, true);
    payload._meta.generatorKey = 'egalite_reconnaitre_liste';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// REGISTRE
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    egalite_relier_disques_relier:         genRelierDisques,
    egalite_axes_gradues_relier:           genRelierAxes,
    egalite_vrai_faux_tableau_vf:          genVraiFaux,
    egalite_decomposition_texte_trou_saisie: genDecompositionTts,
    egalite_decomposition_texte:           genDecompositionSaisie,
    egalite_decomposition_qcm_unique:      genDecompositionQcm,
    egalite_trou_direct_qcm_unique:        genTrouDirectQcm,
    egalite_trou_direct_texte:             genTrouDirectSaisie,
    egalite_trou_entier_qcm_unique:        genTrouEntierQcm,
    egalite_trou_entier_texte:             genTrouEntierSaisie,
    egalite_classer_valeur_classer:        genClasserValeur,
    egalite_reconnaitre_liste_qcm_multiple: genReconnaitreListe,
};

// ═══════════════════════════════════════════════════════════
// CATEGORIES (obligatoire pour l'affichage des cards)
// ═══════════════════════════════════════════════════════════

const FORMAT_LABELS = {
    qcm_unique: 'QCM',
    qcm_multiple: 'QCM Multi',
    texte: 'Saisie',
    texte_trou_drag: 'Drag',
    texte_trou_saisie: 'Saisie trous',
    relier: 'Relier',
    tableau_vf: 'Vrai/Faux',
    classer: 'Classer',
    ordonner: 'Ordonner',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS)
        .filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'egalite_relier_disques',    label: 'Relier des disques fractionnés',     formatsList: buildFormats('egalite_relier_disques') },
    { id: 'egalite_axes_gradues',      label: 'Axes gradués : points alignés',      formatsList: buildFormats('egalite_axes_gradues') },
    { id: 'egalite_vrai_faux',         label: 'Vrai ou faux : = ou ≠ ?',             formatsList: buildFormats('egalite_vrai_faux') },
    { id: 'egalite_decomposition',     label: 'Compléter avec décomposition',       formatsList: buildFormats('egalite_decomposition') },
    { id: 'egalite_trou_direct',       label: 'Compléter une égalité à trou',       formatsList: buildFormats('egalite_trou_direct') },
    { id: 'egalite_trou_entier',       label: 'Compléter avec un entier',           formatsList: buildFormats('egalite_trou_entier') },
    { id: 'egalite_classer_valeur',    label: 'Classer selon la valeur',            formatsList: buildFormats('egalite_classer_valeur') },
    { id: 'egalite_reconnaitre_liste', label: 'Reconnaître dans une liste',         formatsList: buildFormats('egalite_reconnaitre_liste') },
];

// ═══════════════════════════════════════════════════════════
// ENTRÉE PRINCIPALE
// ═══════════════════════════════════════════════════════════

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    // Pas de calculatrice : les égalités se voient par ×k dans les tables
    // (arbitrage user 2026-08-02, même règle que 620-626)
    payload.contenu = { ...payload.contenu, calculatrice: false };
    // Toujours forcer la clé complète (les générateurs ne posent que le préfixe
    // catégorie ce qui cassait le "regenerate dans le même format")
    payload._meta.generatorKey = key;
    return payload;
};

// Exports internes pour les tests (ne pas utiliser en production).
export const _internals = {
    POOL_A, POOL_B, POOL_C, POOL_D,
    GENERATORS, CATEGORIES,
    gcd, isIrreducible, fracEqual, fmtFrac, fmtKatex, fmtItem,
    pickPoolA, pickK,
    buildDisqueSvg, buildDisquesRelierSvg, buildAxesGraduesSvg,
};
